import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MdOutlineHome as HomeIcon,
  MdOutlineBusinessCenter as ConsultantIcon,
  MdOutlineBarChart as EvidenceIcon,
  MdOutlineBiotech as AnalysisIcon,
  MdOutlineSave as SaveIcon,
  MdOutlineExitToApp as ExitIcon,
  MdOutlineHelp as HelpIcon,
  MdOutlineSupportAgent as SupportIcon,
  MdOutlineBookmarks as SnapshotIcon,
  MdOutlineRestartAlt as ResetFiltersIcon,
  MdRule as SaveDiscoveryRulesIcon,
  MdOutlineLightbulb as SaveFindingIcon,
  MdOutlineAssignmentTurnedIn as ActionsIcon,
  MdFileDownload as ExportHtmlIcon,
  MdKeyboardArrowDown as ChevronDownIcon,
  MdKeyboardArrowUp as ChevronUpIcon,
} from 'react-icons/md';

import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { Event } from '@process-advisor/process-miner/api/events';
import { getFindingContext } from '@process-advisor/process-miner/shared/findingContext';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { saveModel, setActiveAction, generateExportData, getTaskArray, getVariantArrayUnfiltered, getActivityArray, getStepArray, getEventLogArray, getModelID as getProcessMinerModelID, getProcessModelTitle, getModelMetricsTotal, getConsultingWorkspace, getSourceSystem } from '@process-advisor/process-miner/processMiner/processMiner';

import PO_Icon from '@process-advisor/assets/processOptimizerHome.png'
import CatworkxIcon from '@process-advisor/assets/Catworkx.png'
import EleganceGroupIcon from '@process-advisor/assets/EleganceGroup.png'
import EnreapIcon from '@process-advisor/assets/Enreap.png'

import DesignIndustriesIcon from '@process-advisor/assets/design_industries.png'
import TrundlIcon from '@process-advisor/assets/trundl.png'
import SVAIcon from '@process-advisor/assets/sva.png'
import KnowmadMoodIcon from '@process-advisor/assets/knowmadmood.png'

import AppHeader from '@advisor-core/components/AppHeader';
import { getCapabilitiesForSource } from '@process-advisor/process-miner/sources/capabilities';
import { getBillingCreditStatus, subscribeBillingStatus, authorizeAssessmentCreditForCurrentDataset } from '@process-advisor/process-miner/consultant/aiBillingAccess';

const C = {
  shellBg: '#F7F8FA',
  shellBorder: '#DFE1E6',
  headerBg: '#FFFFFF',
  title: '#172B4D',
  body: '#344563',
  muted: '#6B778C',
  primary: '#0C66E4',
  primaryTint: '#E9F2FF',
  primaryBorder: '#B3D4FF',
  panelBg: '#F4F5F7',
  panelBorder: '#E5E8EC',
  white: '#FFFFFF',
  findingBg: '#E9F2FF',
  findingBorder: '#85B8FF',
  findingText: '#0C66E4',
  dangerTint: '#FCEBEC',
  dangerBorder: '#F5C2C7',
  dangerText: '#C9372C',
};


let forgeBridgeModule = null;
const loadForgeBridge = async () => {
  if (!forgeBridgeModule) {
    try {
      forgeBridgeModule = await import('@forge/bridge');
    } catch (_) {
      forgeBridgeModule = null;
    }
  }
  return forgeBridgeModule;
};

const SUB_TAB_TO_MODE = {
  home: 'home',
  quickHealthcheck: 'quickHealthcheck',
  processHealth: 'processHealth',
  trendAnalysis: 'trendAnalysis',
  processInvestigations: 'processInvestigations',
  aiIntelligence: 'aiIntelligence',
  aiExecutionStrategy: 'aiExecutionStrategy',
  automationPacks: 'automationPacks',
  frameworkAssessment: 'frameworkAssessment',
  consultingReport: 'consultingReport',
  consultingWorkspace: 'consultingWorkspace',
  workbenchBrief: 'workbenchBrief',
  workbenchFindings: 'workbenchFindings',
  workbenchActions: 'workbenchActions',
  valueSimulation: 'valueSimulation',
  processSnapshot: 'processSnapshot',
  metrics: 'metrics',
  activityMetrics: 'activityMetrics',
  slaMetrics: 'slaMetrics',
  segments: 'segments',
  variants: 'variants',
  transitions: 'transitions',
  performance_discovery_rules: 'performance_discovery_rules',
  compliance_discovery_rules: 'compliance_discovery_rules',
  discovery_rules_visualizer: 'discovery_rules_visualizer',
  process_miner: 'process_miner',
  time_in_activity: 'time_in_activity',
  workflow: 'workflow',
  cluster_insights: 'cluster_insights',
  decomposition_tree: 'decomposition_tree',
  influence_analysis: 'influence_analysis',
  analytics: 'analytics',
  model: 'model',
};

const sleep = ms => new Promise(r => setTimeout(r, ms));

const SUPPORT_PORTAL_URL = 'https://metricus.atlassian.net/servicedesk/customer/portal/3';

const openSupportPortal = () => {
  if (typeof window !== 'undefined') {
    window.open(SUPPORT_PORTAL_URL, '_blank', 'noopener,noreferrer');
  }
};

const ASSESSMENT_CREDIT_VALID_DAYS = 60;
const ASSESSMENT_CREDIT_VALID_MS = ASSESSMENT_CREDIT_VALID_DAYS * 24 * 60 * 60 * 1000;

const parseAssessmentCreditDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isFinite(date.getTime()) ? date : null;
};

const getAssessmentCreditAuthorizedAt = (source = {}) => (
  source?.authorizedAt
  || source?.ai_credit_authorized_at
  || source?.assessmentCreditAuthorizedAt
  || source?.createdAt
  || source?.authorization?.createdAt
  || source?.authorization?.authorizedAt
  || source?.authorization?.marker?.createdAt
  || source?.marker?.createdAt
  || source?.assessmentCreditMarker?.createdAt
  || source?.assessmentCreditMarker?.authorizedAt
  || null
);

const getAssessmentCreditExpiresAt = (source = {}) => {
  const explicit = parseAssessmentCreditDate(
    source?.expiresAt
    || source?.validUntil
    || source?.assessmentCreditExpiresAt
    || source?.authorization?.expiresAt
    || source?.authorization?.marker?.expiresAt
    || source?.marker?.expiresAt
    || source?.assessmentCreditMarker?.expiresAt
    || source?.assessmentCreditMarker?.assessmentCreditExpiresAt
  );
  if (explicit) return explicit;
  const authorizedAt = parseAssessmentCreditDate(getAssessmentCreditAuthorizedAt(source));
  return authorizedAt ? new Date(authorizedAt.getTime() + ASSESSMENT_CREDIT_VALID_MS) : null;
};

const getAssessmentCreditDaysRemaining = (source = {}) => {
  const expiresAt = getAssessmentCreditExpiresAt(source?.assessmentCreditMarker || source);
  if (!expiresAt) return null;
  return Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
};


const getCurrentCapabilities = () => getCapabilitiesForSource(getSourceSystem ? getSourceSystem() : 'generic');


const loadForgeModule = async () => {
  const forgeModule = await import('@forge/bridge');
  return forgeModule;
};

const notifyConsultantTabChange = (tabId) => {
  if (typeof window === 'undefined') return;
  const consultantTabs = new Set([
    'quickHealthcheck',
    'processHealth',
    'trendAnalysis',
    'processInvestigations',
    'aiIntelligence',
    'aiExecutionStrategy',
    'automationPacks',
    'frameworkAssessment',
    'consultingReport',
    'consultingWorkspace',
    'workbenchBrief',
    'workbenchFindings',
    'workbenchActions',
    'processSnapshot',
  ]);
  if (!consultantTabs.has(tabId)) return;
  window.dispatchEvent(new CustomEvent('metricus:consultant-tab-change', {
    detail: { tabId },
  }));
};

const triggerApiForMode = async (subTabId) => {
  if (subTabId === 'home') return;

  if (
    subTabId === 'quickHealthcheck' ||
    subTabId === 'processHealth' ||
    subTabId === 'trendAnalysis' ||
    subTabId === 'processInvestigations' ||
    subTabId === 'aiIntelligence' ||
    subTabId === 'aiExecutionStrategy' ||
    subTabId === 'automationPacks' ||
    subTabId === 'frameworkAssessment' ||
    subTabId === 'consultingReport' ||
    subTabId === 'consultingWorkspace' ||
    subTabId === 'workbenchBrief' ||
    subTabId === 'workbenchFindings' ||
    subTabId === 'workbenchActions' ||
    subTabId === 'processSnapshot'
  ) {
    // AppShell owns the Consultant overlay. Avoid emitting the Consultant
    // action event here, otherwise the always-mounted background Visualizer
    // also renders a hidden ConsultantDashboard on large datasets.
    setActiveAction('consultant');
    return;
  }

  const capabilities = getCurrentCapabilities();
  if ((subTabId === 'activityMetrics' && !capabilities.activityMetrics) || (subTabId === 'slaMetrics' && !capabilities.slaMetrics) || (subTabId === 'segments' && !capabilities.evidenceSegments)) {
    subTabId = 'metrics';
  }

  if (subTabId === 'metrics' || subTabId === 'activityMetrics' || subTabId === 'slaMetrics' || subTabId === 'segments' || subTabId === 'variants' || subTabId === 'transitions') {
    // AppShell owns the Evidence overlay. Avoid emitting the Dashboard action
    // event here, otherwise the background Visualizer also renders a hidden
    // ProcessDashboard and duplicates expensive metrics processing.
    setActiveAction('dashboard');
    return;
  }

  if (subTabId === 'performance_discovery_rules') {
    apiProvider.setAction_DiscoveryRulesSummary({});
    return;
  }

  if (subTabId === 'compliance_discovery_rules') {
    apiProvider.setAction_DiscoveryRulesSummary({});
    return;
  }

  if (subTabId === 'discovery_rules_visualizer') {
    apiProvider.setAction_DiscoveryRulesVisualizer({});
    return;
  }

  if (subTabId === 'process_miner') {
    await sleep(10);
    apiProvider.setAction_Visualizer({});
    apiProvider.setDiagramVisualizer();
    setActiveAction('process_miner');
    return;
  }

  if (subTabId === 'time_in_activity') {
    await openProgressModal({ status: 'Processing Activity Analysis' });
    await sleep(50);
    await apiProvider.generateTasksFilteredArray({
      variantPaneVariants: false,
      type: 'tia',
      variant: 0,
      activity: 0,
      from: 0,
      to: 0,
      discovery_rule_id: 0,
    });
    apiProvider.setAction_Time({});
    setActiveAction('time_in_activity');
    closeProgressModal();
    return;
  }

  if (subTabId === 'decomposition_tree') {
    apiProvider.setAction_DecompositionTree({});
    apiProvider.setDiagramDecompositionTree({ source: 'dt' });
    return;
  }

  if (subTabId === 'cluster_insights') {
    await openProgressModal({ status: 'Processing Clusters' });
    await sleep(50);
    apiProvider.setAction_Cluster({});
    await apiProvider.setDiagramCluster({ source: 'rc' });
    closeProgressModal();
    return;
  }

  if (subTabId === 'influence_analysis') {
    await openProgressModal({ status: 'Processing Influencer Insights' });
    await sleep(50);
    apiProvider.setAction_InfluenceAnalysis({});
    await apiProvider.setDiagramInfluenceAnalysis({ source: 'ia' });
    closeProgressModal();
    return;
  }

  if (subTabId === 'workflow') {
    apiProvider.setAction_Workflow({});
    return;
  }

  if (subTabId === 'analytics') {
    apiProvider.setAction_Analytics({});
    return;
  }

  if (subTabId === 'valueSimulation') {
    setActiveAction('valueSimulation');
    return;
  }

  if (subTabId === 'model') {
    apiProvider.setAction_Model({});
  }
};

const PRIMARY_DEFAULT = {
  home: 'home',
  consultant: 'quickHealthcheck',
  evidence: 'metrics',
  analysis: 'process_miner',
  actions: 'consultingWorkspace',
  value: 'valueSimulation',
  model: 'model',
};

const MODE_TO_PRIMARY = {
  home: 'home',
  quickHealthcheck: 'consultant',
  processHealth: 'consultant',
  trendAnalysis: 'consultant',
  processInvestigations: 'consultant',
  aiIntelligence: 'consultant',
  aiExecutionStrategy: 'consultant',
  automationPacks: 'consultant',
  frameworkAssessment: 'consultant',
  consultingReport: 'actions',
  consultingWorkspace: 'actions',
  workbenchBrief: 'actions',
  workbenchFindings: 'actions',
  workbenchActions: 'actions',
  processSnapshot: 'consultant',
  consultant: 'consultant',
  metrics: 'evidence',
  activityMetrics: 'evidence',
  slaMetrics: 'evidence',
  segments: 'evidence',
  variants: 'evidence',
  transitions: 'evidence',
  performance_discovery_rules: 'evidence',
  compliance_discovery_rules: 'evidence',
  dashboard: 'evidence',
  process_miner: 'analysis',
  time_in_activity: 'analysis',
  discovery_rules_visualizer: 'analysis',
  workflow: 'analysis',
  cluster_insights: 'analysis',
  decomposition_tree: 'analysis',
  influence_analysis: 'analysis',
  analytics: 'analysis',
  valueSimulation: 'value',
  model: 'model',
};

const MODE_TO_SUBTAB = {
  home: 'home',
  quickHealthcheck: 'quickHealthcheck',
  processHealth: 'processHealth',
  trendAnalysis: 'trendAnalysis',
  processInvestigations: 'processInvestigations',
  aiIntelligence: 'aiIntelligence',
  aiExecutionStrategy: 'aiExecutionStrategy',
  automationPacks: 'automationPacks',
  frameworkAssessment: 'frameworkAssessment',
  consultingReport: 'consultingReport',
  consultingWorkspace: 'consultingWorkspace',
  workbenchBrief: 'workbenchBrief',
  workbenchFindings: 'workbenchFindings',
  workbenchActions: 'workbenchActions',
  valueSimulation: 'valueSimulation',
  processSnapshot: 'processSnapshot',
  consultant: 'quickHealthcheck',
  metrics: 'metrics',
  activityMetrics: 'activityMetrics',
  slaMetrics: 'slaMetrics',
  segments: 'segments',
  variants: 'variants',
  transitions: 'transitions',
  performance_discovery_rules: 'performance_discovery_rules',
  compliance_discovery_rules: 'compliance_discovery_rules',
  discovery_rules_visualizer: 'discovery_rules_visualizer',
  dashboard: 'metrics',
  process_miner: 'process_miner',
  time_in_activity: 'time_in_activity',
  workflow: 'workflow',
  cluster_insights: 'cluster_insights',
  decomposition_tree: 'decomposition_tree',
  influence_analysis: 'influence_analysis',
  analytics: 'analytics',
  model: 'model',
};

// ── Advisor sub-tabs: grouped into logical categories ───────────────────────
// Each group has a label (shown as the dropdown trigger) and children (the
// actual sub-tabs). Single-item groups render as a flat button, not a dropdown.

const ADVISOR_GROUPS = [
  {
    label: 'Health',
    children: [
      { id: 'quickHealthcheck',    label: 'Quick Healthcheck' },
      { id: 'processHealth',       label: 'Process Health' },
      { id: 'trendAnalysis',       label: 'Trend Analysis' },
    ],
  },
  {
    label: 'Opportunities',
    children: [
      { id: 'aiIntelligence',      label: 'AI, Automation & Optimisation' },
      { id: 'aiExecutionStrategy', label: 'Execution Strategy' },
      { id: 'automationPacks',     label: 'Automation Packs' },
    ],
  },
  {
    label: 'Frameworks',
    children: [
      { id: 'frameworkAssessment', label: 'Frameworks' },
    ],
  },
  {
    label: 'Ask Advisor',
    children: [
      { id: 'processInvestigations', label: 'Ask Advisor' },
    ],
  },
];

// Flat list for lookups
const ALL_ADVISOR_SUBS = ADVISOR_GROUPS.flatMap(g => g.children);

// ── Explorer sub-tabs: grouped into logical categories ──────────────────────

const EXPLORER_GROUPS = [
  {
    label: 'Process',
    children: [
      { id: 'process_miner',    label: 'Process Miner' },
      { id: 'time_in_activity', label: 'Activity Time' },
    ],
  },
  {
    label: 'Discovery',
    children: [
      { id: 'discovery_rules_visualizer', label: 'Discovery Rules' },
    ],
  },
  {
    label: 'Root Cause',
    children: [
      { id: 'cluster_insights',   label: 'Clustering',          requiresAttrs: true },
      { id: 'decomposition_tree', label: 'Decomposition Tree',  requiresAttrs: true },
      { id: 'influence_analysis', label: 'Influencers',         requiresAttrs: true },
    ],
  },
  {
    label: 'Analytics',
    children: [
      { id: 'analytics', label: 'Analytics' },
    ],
  },
];

const ALL_EXPLORER_SUBS = EXPLORER_GROUPS.flatMap(g => g.children);

const NAV = [
  { id: 'home', label: 'Home', icon: HomeIcon, subTabs: [{ id: 'home', label: 'Overview' }] },
  {
    id: 'evidence',
    label: 'Evidence',
    icon: EvidenceIcon,
    subTabs: [
      { id: 'metrics', label: 'Process Metrics' },
      { id: 'slaMetrics', label: 'SLA Metrics', capability: 'slaMetrics' },
      { id: 'segments', label: 'Segments', capability: 'evidenceSegments' },
      { id: 'variants', label: 'Variants' },
      { id: 'transitions', label: 'Transitions' },
      { id: 'performance_discovery_rules', label: 'Performance Rules' },
      { id: 'compliance_discovery_rules', label: 'Compliance Rules' },
    ],
  },
  {
    id: 'analysis',
    label: 'Explorer',
    icon: AnalysisIcon,
    subTabs: ALL_EXPLORER_SUBS,
    grouped: true,
    groups: EXPLORER_GROUPS,
  },
  {
    id: 'consultant',
    label: 'Advisor',
    icon: ConsultantIcon,
    // subTabs kept for backward compat (triggerApiForMode, MODE_TO_PRIMARY lookups)
    subTabs: ALL_ADVISOR_SUBS,
    grouped: true,
  },
  {
    id: 'value',
    label: 'Value',
    icon: EvidenceIcon,
    subTabs: [
      { id: 'valueSimulation', label: 'Value Simulation', capability: 'valueSimulation' },
    ],
  },
  {
    id: 'actions',
    label: 'Assessment',
    icon: ActionsIcon,
    subTabs: [
      { id: 'consultingWorkspace', label: 'Dashboard' },
      { id: 'workbenchBrief', label: 'Engagement Brief' },
      { id: 'workbenchFindings', label: 'Findings' },
      { id: 'workbenchActions', label: 'Actions' },
      { id: 'consultingReport', label: 'Assessment Report' },
    ],
  },
];


const getSavedFindingCount = () => {
  let count = 0;
  try {
    const memory = getConsultingWorkspace ? getConsultingWorkspace() : null;
    if (Array.isArray(memory?.findings)) count = Math.max(count, memory.findings.length);
  } catch (_) {}
  try {
    const modelId = getProcessMinerModelID ? getProcessMinerModelID() : '';
    const raw = window.localStorage.getItem(`metricus.consultingWorkspace.${modelId || 'unsaved'}`);
    if (raw) {
      const local = JSON.parse(raw);
      if (Array.isArray(local?.findings)) count = Math.max(count, local.findings.length);
    }
  } catch (_) {}
  return count;
};

const SECTION_COPY = {
  home: 'Start here to understand the current model and where to go next.',
  consultant: 'AI-generated assessments, strategy, and automation recommendations.',
  evidence: 'Metrics, activity metrics, SLA metrics, segments, variants, transitions, and discovery rules behind the recommendations.',
  analysis: 'Advanced process mining, detailed discovery rules, and root-cause analysis tools for deeper investigation.',
  actions: 'Build the assessment deliverable: engagement brief, curated findings, actions, and final client-ready report.',
};


// ── Grouped dropdown for Advisor sub-tabs ───────────────────────────────────

const GroupDropdown = ({ group, activeSub, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isGroupActive = group.children.some(c => c.id === activeSub);
  const activeChild = group.children.find(c => c.id === activeSub);

  // Single-item group: render as flat button
  if (group.children.length === 1) {
    const child = group.children[0];
    const active = child.id === activeSub;
    return (
      <button
        onClick={() => onSelect(child.id)}
        style={{
          minHeight: 34,
          padding: '0 12px',
          borderRadius: 8,
          border: active ? `1px solid ${C.primaryBorder}` : '1px solid transparent',
          background: active ? '#FFFFFF' : 'transparent',
          color: active ? C.primary : C.body,
          fontSize: 13,
          fontWeight: active ? 700 : 600,
          cursor: 'pointer',
        }}
      >
        {child.label}
      </button>
    );
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Keep the trigger label stable as the parent group name.
  // The selected child is highlighted inside the dropdown.
  const displayLabel = group.label;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          minHeight: 34,
          padding: '0 10px 0 12px',
          borderRadius: 8,
          border: isGroupActive ? `1px solid ${C.primaryBorder}` : '1px solid transparent',
          background: isGroupActive ? '#FFFFFF' : 'transparent',
          color: isGroupActive ? C.primary : C.body,
          fontSize: 13,
          fontWeight: isGroupActive ? 700 : 600,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {displayLabel}
        <ChevronDownIcon size={18} style={{
          transition: 'transform 0.15s',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          opacity: 0.6,
        }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          minWidth: 200,
          background: '#FFFFFF',
          border: `1px solid ${C.panelBorder}`,
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(9,30,66,.15), 0 0 1px rgba(9,30,66,.1)',
          zIndex: 100,
          padding: '4px 0',
        }}>
          {group.children.map(child => {
            const active = child.id === activeSub;
            return (
              <button
                key={child.id}
                onClick={() => { onSelect(child.id); setOpen(false); }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 14px',
                  border: 'none',
                  background: active ? C.primaryTint : 'transparent',
                  color: active ? C.primary : C.body,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#F4F5F7'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                {child.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};


const AppNav = ({
  currentMode = 'home',
  customer,
  onModeChange,
  onSave,
  onExit,
  onHelp,
  appTitle = 'Process Advisor',
  modelTitle = '',
  hasTaskAttributes = false,
  AppVersion,
  appEdition = 'standard',
  editionAccess = null,
  snapshotCount = 0,
  onSnapshotsClick = null,
  hidePrimaryHeader = false,
  externalModeRequest = '',
}) => {
  const [activePrimary, setActivePrimary] = useState('home');
  const [activeSub, setActiveSub] = useState('home');
  const [filterIconActive, setFilterIconActive] = useState(false);
  const [discoveryIconActive, setDiscoveryIconActive] = useState(false);
  const [findingCount, setFindingCount] = useState(0);
  const [workbenchSaveState, setWorkbenchSaveState] = useState('saved');
  const [editionInfoOpen, setEditionInfoOpen] = useState(false);
  const [billingCreditStatus, setBillingCreditStatus] = useState(null);
  const [billingCreditsLoading, setBillingCreditsLoading] = useState(false);
  const [assessmentPublicKey, setAssessmentPublicKey] = useState('');
  const [assessmentPrivateKey, setAssessmentPrivateKey] = useState('');
  const [assessmentKeySaving, setAssessmentKeySaving] = useState(false);
  const [assessmentApplyStep, setAssessmentApplyStep] = useState('');
  const [assessmentKeyMessage, setAssessmentKeyMessage] = useState(null);
  const [accessSuccessMessage, setAccessSuccessMessage] = useState(null);
  const [runtimeEditionAccess, setRuntimeEditionAccess] = useState(editionAccess || null);
  const [headerCollapsed, setHeaderCollapsed] = useState(() => {
    try { return window.localStorage.getItem('metricus.headerCollapsed') === 'true'; } catch (_) { return false; }
  });

  const toggleHeader = () => {
    setHeaderCollapsed(prev => {
      const next = !prev;
      try { window.localStorage.setItem('metricus.headerCollapsed', String(next)); } catch (_) {}
      return next;
    });
  };

  useEffect(() => {
    setRuntimeEditionAccess(prev => ({ ...(prev || {}), ...(editionAccess || {}) }));
  }, [editionAccess]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onAssessmentCreditAuthorized = (event) => {
      const detail = event?.detail || {};
      setRuntimeEditionAccess(prev => ({
        ...(prev || editionAccess || {}),
        assessmentCreditAuthorized: true,
        hasAssessmentCreditAuthorization: true,
        datasetAiUnlocked: true,
        aiCreditAuthorized: true,
        isUsageLimited: false,
        isLimitedAccess: false,
        enforceWorkItemLimit: false,
        workItemLimit: null,
        taskLimit: null,
        showPlanBadge: false,
        showLimitLine: false,
        assessmentCreditMarker: detail.authorization || detail || null,
        assessmentCreditAuthorizedAt: getAssessmentCreditAuthorizedAt(detail.authorization?.marker || detail.authorization || detail) || new Date().toISOString(),
        assessmentCreditExpiresAt: getAssessmentCreditExpiresAt(detail.authorization?.marker || detail.authorization || detail)?.toISOString?.() || null,
      }));
    };
    window.addEventListener('metricus-assessment-credit-authorized', onAssessmentCreditAuthorized);
    return () => window.removeEventListener('metricus-assessment-credit-authorized', onAssessmentCreditAuthorized);
  }, [editionAccess]);


  useEffect(() => {
    const refreshFindingCount = () => setFindingCount(getSavedFindingCount());
    refreshFindingCount();
    if (typeof window !== 'undefined') {
      window.addEventListener('metricus:workspace-updated', refreshFindingCount);
      window.addEventListener('metricus:consulting-workspace-updated', refreshFindingCount);
      window.addEventListener('storage', refreshFindingCount);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('metricus:workspace-updated', refreshFindingCount);
        window.removeEventListener('metricus:consulting-workspace-updated', refreshFindingCount);
        window.removeEventListener('storage', refreshFindingCount);
      }
    };
  }, [currentMode, modelTitle]);

  const checkFiltersDiscovery = useCallback(() => {
    setFilterIconActive(false);
    setDiscoveryIconActive(false);
    const filterConditions = apiProvider.filterConditions({ category: 'all' });
    if (!filterConditions || !Array.isArray(filterConditions)) return;
    let filterExists = false;
    let discoveryExists = false;
    for (let i = 0; i < filterConditions.length; i += 1) {
      if (filterConditions[i]?.type === 'filter') filterExists = true;
      if (filterConditions[i]?.type === 'discovery') discoveryExists = true;
    }
    setFilterIconActive(filterExists);
    setDiscoveryIconActive(discoveryExists);
  }, []);

  useEffect(() => {
    const activateFilter = () => setFilterIconActive(true);
    const activateDiscovery = () => setDiscoveryIconActive(true);
    const refreshState = () => checkFiltersDiscovery();

    apiProvider.on(Event.filterIconActive, activateFilter);
    apiProvider.on(Event.discoveryIconActive, activateDiscovery);
    apiProvider.on(Event.resetFilter, refreshState);
    apiProvider.on(Event.resetDiscovery, refreshState);

    // Navigation listeners for diagram events
    const navigateToCluster = () => {
      setActivePrimary('analysis');
      setActiveSub('cluster_insights');
    };
    const navigateToInfluence = () => {
      setActivePrimary('analysis');
      setActiveSub('influence_analysis');
    };
    const navigateToDecomposition = () => {
      setActivePrimary('analysis');
      setActiveSub('decomposition_tree');
    };
    const navigateToProcessMiner = () => {
      setActivePrimary('analysis');
      setActiveSub('process_miner');
      if (typeof onModeChange === 'function') onModeChange('process_miner');
    };

    apiProvider.on(Event.setDiagramCluster, navigateToCluster);
    apiProvider.on(Event.setDiagramInfluenceAnalysis, navigateToInfluence);
    apiProvider.on(Event.setDiagramDecompositionTree, navigateToDecomposition);
    apiProvider.on(Event.processDiscoveryRule, navigateToProcessMiner);
    apiProvider.on(Event.processFinding, navigateToProcessMiner);

    refreshState();

    return () => {
      apiProvider.off(Event.filterIconActive, activateFilter);
      apiProvider.off(Event.discoveryIconActive, activateDiscovery);
      apiProvider.off(Event.resetFilter, refreshState);
      apiProvider.off(Event.resetDiscovery, refreshState);
      apiProvider.off(Event.setDiagramCluster, navigateToCluster);
      apiProvider.off(Event.setDiagramInfluenceAnalysis, navigateToInfluence);
      apiProvider.off(Event.setDiagramDecompositionTree, navigateToDecomposition);
      apiProvider.off(Event.processDiscoveryRule, navigateToProcessMiner);
      apiProvider.off(Event.processFinding, navigateToProcessMiner);
    };
  }, [checkFiltersDiscovery]);

  const handleResetFilters = async () => {
    if (!(filterIconActive || discoveryIconActive)) return;
    apiProvider.setAction_Visualizer({});
    await openProgressModal({ status: 'Resetting Filters and Discovery' });
    await apiProvider.resetFilters({ regenerateArray: true });
    apiProvider.updateVariantSlicerVariantChart({});
    closeProgressModal();
  };


  const handleSaveDiscoveryRules = () => {
    if (!(filterIconActive || discoveryIconActive)) return;
    apiProvider.saveDiscoveryRuleModal();
  };


  const handleSaveFinding = () => {
    const visibleSubTabs = activePrimaryMeta.subTabs.filter(s => (!s.requiresAttrs || hasTaskAttributes) && (!s.capability || getCurrentCapabilities()[s.capability]));
    const sourceLabel = visibleSubTabs.find(s => s.id === activeSub)?.label || activeSub || 'Current view';
    const selectedContext = getFindingContext?.();
    const contextMatchesView = !selectedContext?.source || selectedContext.source === sourceLabel || activePrimary === 'analysis';
    const objectContext = contextMatchesView ? selectedContext : null;
    const objectLabel = objectContext?.objectLabel || '';
    const objectType = objectContext?.objectType || '';
    const titleBase = objectLabel || sourceLabel;
    const data = {
      mode: 'new',
      source: sourceLabel,
      objectType,
      objectLabel,
      objectId: objectContext?.objectId || '',
      objectRow: objectContext?.row || null,
      tasks: 0,
      events: 0,
      findingID: 0,
      finding: `${titleBase} finding`,
      title: `${titleBase} finding`,
      category: objectContext?.category || 'Performance',
      calculate_cost_impact: false,
      filters: objectContext?.filters || [],
      filterSummary: objectContext?.filterSummary || [],
      automation_opportunity: false,
      type: 'Manual Finding',
      finding_type: 'consulting',
      save_activities: false,
      save_steps: false,
      save_variants: false,
      filter_activities: '',
      filter_variants: '',
      filter_steps: '',
    };

    apiProvider.eventEmitter.emit(Event.openFindingModal, { data });
  };

  const handleExportHtml = async () => {
    try {
      await openProgressModal({ status: 'Generating HTML Report...' });

      const exportData = generateExportData({
        activityArray: getActivityArray(),
        stepArray:     getStepArray(),
        variantArray:  getVariantArrayUnfiltered(),
        taskArray:     getTaskArray(),
        eventLogArray: getEventLogArray(),
      });
      const { variants, steps, diagramData } = exportData;

      const issues = getTaskArray().map(t => ({
        key:      t.task     || '',
        summary:  t.summary  || '',
        type:     t.issueType || t.type || '',
        status:   t.status   || '',
        created:  t.created  || '',
        resolved: t.resolved || '',
        duration: t.duration || 0,
      }));

      const modelName = getProcessModelTitle() || 'Report';
      const model = { name: modelName, id: getProcessMinerModelID() };

      const htmlModule = await import('@process-advisor/lib/htmlReportGenerator');
      const { generateProcessReportHTML } = htmlModule;
      const html = generateProcessReportHTML(model, variants, steps, issues, diagramData, undefined, undefined, undefined, [], [], [], getModelMetricsTotal());

      const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href  = url;
      link.download = `${modelName.replace(/[^a-z0-9]/gi, '_')}-report-${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      closeProgressModal();
    } catch (error) {
      console.error('Error exporting to HTML:', error);
      closeProgressModal();
    }
  };

  const handleSaveModel = async () => {
    await saveModel(AppVersion);
  };

  useEffect(() => {
    if (!currentMode) return;
    setActivePrimary(MODE_TO_PRIMARY[currentMode] || 'home');
    setActiveSub(MODE_TO_SUBTAB[currentMode] || 'home');
  }, [currentMode]);

  const handleSubClick = async (subTabId) => {
    const modeStr = SUB_TAB_TO_MODE[subTabId] || 'home';
    const nextPrimary = NAV.find(p => p.subTabs.some(s => s.id === subTabId))?.id || 'home';

    if (nextPrimary === 'consultant' || nextPrimary === 'actions' || nextPrimary === 'report') {
      try { window.localStorage.setItem('metricus.consultant.activeTab', subTabId); } catch (_) {}
      notifyConsultantTabChange(subTabId);
    }

    setActivePrimary(nextPrimary);
    setActiveSub(subTabId);
    onModeChange(modeStr);
    await triggerApiForMode(subTabId);
    if (nextPrimary === 'consultant' || nextPrimary === 'actions' || nextPrimary === 'report') {
      notifyConsultantTabChange(subTabId);
      setTimeout(() => notifyConsultantTabChange(subTabId), 0);
    }
  };

  const handlePrimaryClick = (item) => handleSubClick(PRIMARY_DEFAULT[item.id]);

  useEffect(() => {
    if (!externalModeRequest || externalModeRequest === currentMode) return;
    const subTabId = MODE_TO_SUBTAB[externalModeRequest] || externalModeRequest;
    if (!SUB_TAB_TO_MODE[subTabId]) return;
    handleSubClick(subTabId);
  }, [externalModeRequest]);

  const activePrimaryMeta = NAV.find(p => p.id === activePrimary) || NAV[0];
  const visibleSubTabs = activePrimaryMeta.subTabs.filter(s => (!s.requiresAttrs || hasTaskAttributes) && (!s.capability || getCurrentCapabilities()[s.capability]));
  const showSubTabs = !(activePrimary === 'home' && visibleSubTabs.length === 1 && visibleSubTabs[0].id === 'home');

  useEffect(() => {
    const handleWorkbenchSaveState = (event) => {
      setWorkbenchSaveState(event?.detail?.saveState || 'saved');
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('metricus:workbench-save-state', handleWorkbenchSaveState);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('metricus:workbench-save-state', handleWorkbenchSaveState);
      }
    };
  }, []);

  const handleSaveWorkbench = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('metricus:save-workbench'));
  };

  const handleSaveValueScenario = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('metricus:save-value-scenario'));
  };

  const handleSaveValueFinding = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('metricus:save-value-finding'));
  };


  // Should the action buttons row be shown? Only when there are buttons to show.
  const isFreeJiraProduct = typeof window !== 'undefined' && window.__METRICUS_JIRA_PRODUCT_EDITION__ === 'free';

  const showActionRow = (
    (activePrimary === 'consultant' || activePrimary === 'evidence') ||
    activePrimary === 'actions' ||
    activePrimary === 'analysis' ||
    activePrimary === 'value'
  );

  const resolvedIcon = customer === 'Metricus' ? PO_Icon : customer === 'Enreap' ? EnreapIcon : customer === 'Knowmad-Mood' ? KnowmadMoodIcon :  customer === 'EleganceGroup' ? EleganceGroupIcon : customer === 'Trundl' ? TrundlIcon : customer === 'SVA' ? SVAIcon : customer === 'Catworkx' ? CatworkxIcon : customer === 'DesignIndustries' ? DesignIndustriesIcon : PO_Icon;

  const effectiveEditionAccess = runtimeEditionAccess || editionAccess || {};
  const snDisplayValue = (value) => (value && typeof value === 'object' ? (value.value ?? value.display_value ?? '') : value);
  const sampleValue = snDisplayValue(effectiveEditionAccess?.sample);
  const categoryValue = snDisplayValue(effectiveEditionAccess?.category);
  const isSampleDataset = effectiveEditionAccess?.sampleDataset === true
    || effectiveEditionAccess?.isSample === true
    || sampleValue === true
    || sampleValue === 1
    || sampleValue === '1'
    || String(sampleValue || '').trim().toUpperCase() === 'Y'
    || String(sampleValue || '').trim().toLowerCase() === 'true'
    || String(categoryValue || '').trim().toLowerCase() === 'sample';
  const sourceSystem = String(getSourceSystem ? getSourceSystem() : AppVersion || '').trim().toLowerCase();
  const isServiceNowRuntime = ['servicenow', 'snow', 'service_now'].includes(sourceSystem) || String(AppVersion || '').trim().toUpperCase() === 'SNOW';
  const normalizedPlan = (() => {
    const rawPlan = String(effectiveEditionAccess?.plan || effectiveEditionAccess?.pricing || '').toLowerCase();
    const rawEdition = String(effectiveEditionAccess?.edition || appEdition || '').toLowerCase();
    if (['usage', 'usage-based', 'usage_based', 'assessment', 'consultant', 'partner'].includes(rawPlan)) return 'usage';
    // ServiceNow is always usage-priced through Metricus billing. Do not let the
    // shared appEdition default of "standard" make the SN runtime look like the
    // Jira free Marketplace app, otherwise the model-level Assessment Credit
    // button is hidden.
    if (isServiceNowRuntime) return 'usage';
    if (['free', 'standard', 'lite', 'limited'].includes(rawPlan) || rawEdition === 'standard') return 'free';
    return 'subscription';
  })();
  const isFreePlan = normalizedPlan === 'free';
  const isUsagePlan = normalizedPlan === 'usage';
  const billingHasSubscriptionAccess = (() => {
    const nested = billingCreditStatus?.data || billingCreditStatus?.result || {};
    const values = { ...(nested || {}), ...(billingCreditStatus || {}), ...(effectiveEditionAccess || {}) };
    const billingAccess = values.billingAccess || {};
    const appAccess = billingAccess.appAccess || values.appAccess || {};
    const subscriptionAccess = billingAccess.subscriptionAccess || values.subscriptionAccess || {};
    const entitlementType = String(values.entitlementType || values.entitlement_type || values.accessType || values.registrationType || '').toLowerCase();
    const subscriptionStatus = String(values.subscriptionStatus || values.subscription_status || subscriptionAccess.status || '').toLowerCase();
    const subscriptionSource = String(appAccess.source || subscriptionAccess.source || '').toLowerCase();
    const hasSubscriptionIdentifier = Boolean(
      values.serviceInstanceId || values.service_instance_id || values.subscriptionId || values.subscription_id || values.clientSlotId || values.client_slot_id || values.instanceUrl || values.instance_url ||
      subscriptionAccess.serviceInstanceId || subscriptionAccess.service_instance_id || subscriptionAccess.subscriptionId || subscriptionAccess.subscription_id || subscriptionAccess.clientSlotId || subscriptionAccess.client_slot_id || subscriptionAccess.instanceUrl || subscriptionAccess.instance_url
    );
    const isSubscriptionSource = (value) => ['subscription', 'instance_subscription', 'partner_subscription', 'partner_client_slot', 'dedicated_subscription'].includes(String(value || '').toLowerCase());
    return values.annualAccess === true
      || values.subscriptionAccess === true
      || values.hasSubscription === true
      || values.partnerAccess === true
      || values.instanceRegistered === true
      || values.clientSlotRegistered === true
      || values.hasSubscriptionAccess === true
      || appAccess.registered === true
      || (appAccess.mode === 'full' && (hasSubscriptionIdentifier || isSubscriptionSource(appAccess.source)))
      || (subscriptionAccess.active === true && (hasSubscriptionIdentifier || isSubscriptionSource(subscriptionAccess.source || subscriptionAccess.entitlementType || subscriptionAccess.type)))
      || ((subscriptionStatus === 'active' || subscriptionStatus === 'registered') && (hasSubscriptionIdentifier || isSubscriptionSource(subscriptionSource) || isSubscriptionSource(subscriptionAccess.source)))
      || subscriptionSource === 'instance_subscription'
      || subscriptionSource === 'partner_client_slot';
  })();
  const assessmentCreditDaysLeft = getAssessmentCreditDaysRemaining(effectiveEditionAccess || {});
  const assessmentCreditIsExpired = assessmentCreditDaysLeft !== null && assessmentCreditDaysLeft <= 0;
  const hasAssessmentCreditDatasetUnlock = !assessmentCreditIsExpired && (
    effectiveEditionAccess?.assessmentCreditAuthorized === true
    || effectiveEditionAccess?.hasAssessmentCreditAuthorization === true
    || effectiveEditionAccess?.datasetAiUnlocked === true
    || effectiveEditionAccess?.aiCreditAuthorized === true
  );
  const isUsageLimitedPlan = isUsagePlan && !isSampleDataset && !hasAssessmentCreditDatasetUnlock && !billingHasSubscriptionAccess && effectiveEditionAccess?.hasSubscriptionAccess !== true;
  const isTrialPlan = !hasAssessmentCreditDatasetUnlock && normalizedPlan === 'subscription' && (effectiveEditionAccess?.isTrial === true || effectiveEditionAccess?.isEvaluation === true);
  const hasActiveSubscription = !hasAssessmentCreditDatasetUnlock && ((normalizedPlan === 'subscription' && !isTrialPlan) || (isUsagePlan && billingHasSubscriptionAccess));
  const isLimitedPlan = !hasActiveSubscription;
  const itemNoun = sourceSystem === 'servicenow' || sourceSystem === 'snow' || sourceSystem === 'service_now' ? 'tasks' : 'work items';
  const freeLimit = effectiveEditionAccess?.workItemLimit || effectiveEditionAccess?.taskLimit || (isTrialPlan ? 250 : 100);
  const usageLimitedLabel = isServiceNowRuntime ? `100-${itemNoun.replace(/s$/, '')} free view` : `Free Plan - ${freeLimit} ${itemNoun}`;
  const planLabel = hasActiveSubscription ? 'Active Subscription' : usageLimitedLabel;
  const limitLabel = hasActiveSubscription ? 'Active Subscription' : usageLimitedLabel;
  const planTone = hasActiveSubscription
    ? { border: '#ABF5D1', bg: '#E3FCEF', text: '#216E4E', dot: '#22A06B', halo: '#BAF3DB' }
    : { border: '#DFE1E6', bg: '#F7F8F9', text: '#44546F', dot: '#6B778C', halo: '#EBECF0' };

  const billingCreditCount = Number(
    billingCreditStatus?.creditBalance ??
    billingCreditStatus?.assessmentCredits ??
    billingCreditStatus?.availableAssessmentCredits ??
    billingCreditStatus?.credits ??
    0
  ) || 0;

  const refreshBillingCredits = useCallback(async () => {
    // App-level subscriptions can be registered against either the Jira Free app
    // or the usage/billing flow.  Check billing status for both so the header
    // can show Active Subscription as soon as a customer instance subscription
    // or partner slot key is registered.
    // The dedicated Jira Free edition has no Metricus billing integration.
    if (typeof window !== 'undefined' && window.__METRICUS_JIRA_PRODUCT_EDITION__ === 'free') return;
    if (!isUsagePlan && !isFreePlan) return;
    setBillingCreditsLoading(true);
    try {
      const bridge = await loadForgeBridge();
      const status = await getBillingCreditStatus(bridge?.invoke);
      setBillingCreditStatus(status);
      try { window.dispatchEvent(new CustomEvent('metricus-billing-status', { detail: status || {} })); } catch (_) {}
    } catch (error) {
      setBillingCreditStatus({ creditBalance: 0, assessmentCredits: 0, availableAssessmentCredits: 0, error: error?.message || String(error) });
    } finally {
      setBillingCreditsLoading(false);
    }
  }, [AppVersion, isUsagePlan, isFreePlan]);

  useEffect(() => {
    refreshBillingCredits();
  }, [refreshBillingCredits]);

  useEffect(() => {
    const unsubscribe = subscribeBillingStatus((status) => setBillingCreditStatus(status || {}));
    const onBillingStatus = (event) => setBillingCreditStatus(event?.detail || {});
    if (typeof window !== 'undefined') {
      window.addEventListener('metricus-billing-status', onBillingStatus);
      window.addEventListener('metricus:billing-status', onBillingStatus);
    }
    return () => {
      try { unsubscribe(); } catch (_) {}
      if (typeof window !== 'undefined') {
        window.removeEventListener('metricus-billing-status', onBillingStatus);
        window.removeEventListener('metricus:billing-status', onBillingStatus);
      }
    };
  }, []);

  useEffect(() => {
    const existingPublicKey = billingCreditStatus?.publicIntegrationKey || billingCreditStatus?.public_integration_key || billingCreditStatus?.data?.publicIntegrationKey || billingCreditStatus?.data?.public_integration_key || '';
    if (existingPublicKey && !assessmentPublicKey) setAssessmentPublicKey(existingPublicKey);
  }, [billingCreditStatus, assessmentPublicKey]);

  useEffect(() => {
    if (!assessmentKeySaving) {
      setAssessmentApplyStep('');
      return undefined;
    }
    const steps = [
      'Preparing credit authorisation…',
      'Sending keys to the billing portal…',
      'Authorising the Assessment Credit…',
      'Refreshing model access…',
      'Unlocking the full process model…',
    ];
    setAssessmentApplyStep(steps[0]);
    const timers = steps.slice(1).map((step, index) => window.setTimeout(() => setAssessmentApplyStep(step), (index + 1) * 1800));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [assessmentKeySaving]);

  const saveAssessmentCreditKeys = async () => {
    if (!assessmentPublicKey.trim() || !assessmentPrivateKey.trim()) {
      setAssessmentKeyMessage({ type: 'error', text: 'Enter both the public integration key and Assessment Credit key.' });
      return;
    }
    setAssessmentKeySaving(true);
    setAssessmentKeyMessage(null);
    try {
      const bridge = await loadForgeModule();
      const result = await bridge.invoke('saveBillingCredentials', {
        publicIntegrationKey: assessmentPublicKey.trim(),
        publicKey: assessmentPublicKey.trim(),
        instanceKey: assessmentPrivateKey.trim(),
        assessmentCreditKey: assessmentPrivateKey.trim(),
        secretKey: assessmentPrivateKey.trim(),
        privateKey: assessmentPrivateKey.trim(),
        registrationType: 'assessment_credits',
        credentialType: 'assessment_credit',
        accessType: 'assessment_credits',
        entitlementType: 'assessment_credit',
        type: 'assessment_credits',
      });
      const status = await getBillingCreditStatus(bridge?.invoke).catch(() => result || {});
      setBillingCreditStatus(status || result || {});
      try { window.dispatchEvent(new CustomEvent('metricus-billing-status', { detail: status || result || {} })); } catch (_) {}

      const authorization = await authorizeAssessmentCreditForCurrentDataset({
        invoke: bridge?.invoke,
        reportLabel: 'Model unlock',
      });

      const refreshedStatus = await getBillingCreditStatus(bridge?.invoke).catch(() => status || result || {});
      setBillingCreditStatus(refreshedStatus || status || result || {});
      try { window.dispatchEvent(new CustomEvent('metricus-billing-status', { detail: refreshedStatus || status || result || {} })); } catch (_) {}
      setAssessmentPrivateKey('');
      setAssessmentKeyMessage(null);
      setEditionInfoOpen(false);
      const creditsUsed = authorization?.requirement?.requiredCredits || 1;
      const expiresAt = getAssessmentCreditExpiresAt(authorization?.marker || authorization || {});
      const daysLeft = expiresAt ? getAssessmentCreditDaysRemaining(authorization?.marker || authorization || {}) : ASSESSMENT_CREDIT_VALID_DAYS;
      setAccessSuccessMessage(`${creditsUsed} Assessment Credit${creditsUsed === 1 ? '' : 's'} applied. Full model unlocked for ${daysLeft || ASSESSMENT_CREDIT_VALID_DAYS} days.`);
      setTimeout(() => setAccessSuccessMessage(null), 3500);
    } catch (error) {
      setAssessmentKeyMessage({ type: 'error', text: `Unable to apply Assessment Credit: ${error?.message || String(error)}` });
    } finally {
      setAssessmentKeySaving(false);
    }
  };

  const openMetricusPricing = async () => {
    const url = 'https://billing.metricus.com';
    if (AppVersion === 'FORGE') {
      try {
        const bridge = await loadForgeModule();
        if (bridge?.router?.open) {
          bridge.router.open(url);
          return;
        }
      } catch (_) {}
    }
    try { window.open(url, '_blank', 'noopener,noreferrer'); } catch (_) {}
  };

  const openPaidMarketplaceApp = async () => {
    const url = 'https://marketplace.atlassian.com/apps/1230330';
    if (AppVersion === 'FORGE') {
      try {
        const bridge = await loadForgeModule();
        if (bridge?.router?.open) {
          bridge.router.open(url);
          return;
        }
      } catch (_) {}
    }
    try { window.open(url, '_blank', 'noopener,noreferrer'); } catch (_) {}
  };

  const renderEditionInfoModal = () => {
    if (!editionInfoOpen) return null;
    return (
      <div
        onClick={() => { if (!assessmentKeySaving) setEditionInfoOpen(false); }}
        style={{ position: 'fixed', inset: 0, background: 'rgba(9, 30, 66, 0.35)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="pricing-info-title"
          onClick={(event) => event.stopPropagation()}
          style={{ position: 'relative', width: 'min(520px, 100%)', background: C.white, border: `1px solid ${C.shellBorder}`, borderRadius: 12, boxShadow: '0 16px 32px rgba(9, 30, 66, 0.22)', overflow: 'hidden' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid #EBECF0' }}>
            <div id="pricing-info-title" style={{ color: C.title, fontSize: 18, fontWeight: 700 }}>Access & Billing</div>
            <button aria-label="Close" disabled={assessmentKeySaving} onClick={() => { if (!assessmentKeySaving) setEditionInfoOpen(false); }} style={{ border: 0, background: 'transparent', color: C.muted, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
          <div style={{ padding: '18px 20px 20px', color: C.body, fontSize: 14, lineHeight: 1.5 }}>
            <p style={{ marginTop: 0 }}>You are currently using the <strong>{planLabel}</strong>.</p>
            {isFreePlan ?
              <p>The Free Plan is limited to the first 100 work items per model refresh.</p>
            : isTrialPlan ?
              <p>The Atlassian Marketplace trial is limited to the first 250 work items per model refresh.</p>
            : isUsagePlan ?
              <p>This model is currently in the 100-task free view. Apply an Assessment Credit to unlock the full model and AI Advisor outputs for 60 days from the date applied. Assessment Credits are applied per model.</p>
            :
              <p>An active subscription is registered for this instance.</p>
            }
            {isFreePlan && !isServiceNowRuntime ?
              <>
                <p>To upgrade to the Subscription version of Process Advisor, visit the paid Process Advisor app on the Atlassian Marketplace.</p>
                <p style={{ marginBottom: 0 }}>For Assessment Credits visit <strong>billing.metricus.com</strong> or email <strong>info@metricus.com</strong>.</p>
              </>
            :
              <p style={{ marginBottom: 0 }}>For Assessment Credits visit <strong>billing.metricus.com</strong> or email <strong>info@metricus.com</strong>.</p>
            }
            {!hasActiveSubscription && (
              <div style={{ marginTop: 14, padding: '12px 14px', border: '1px solid #CCE0FF', borderRadius: 10, background: '#F4F8FF' }}>
                <div style={{ fontWeight: 800, color: C.title, marginBottom: 6 }}>Assessment Credit keys</div>
                <div style={{ fontSize: 13, color: C.body, marginBottom: 10 }}>Enter the keys from a purchased Assessment Credit account to unlock this model for 60 days. The model home page will show the days remaining.</div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: C.body, marginBottom: 4 }}>Public integration key</label>
                <input
                  value={assessmentPublicKey}
                  onChange={(event) => setAssessmentPublicKey(event.target.value)}
                  placeholder="Paste the public integration key"
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #DFE1E6', borderRadius: 6, padding: '8px 10px', fontSize: 13, marginBottom: 8 }}
                />
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: C.body, marginBottom: 4 }}>Assessment Credit key</label>
                <input
                  type="password"
                  value={assessmentPrivateKey}
                  onChange={(event) => setAssessmentPrivateKey(event.target.value)}
                  placeholder="Paste the Assessment Credit key"
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #DFE1E6', borderRadius: 6, padding: '8px 10px', fontSize: 13 }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={saveAssessmentCreditKeys}
                    disabled={assessmentKeySaving}
                    style={{ border: `1px solid ${C.primary}`, background: C.primary, color: C.white, borderRadius: 6, padding: '8px 12px', fontSize: 13, fontWeight: 700, cursor: assessmentKeySaving ? 'wait' : 'pointer', opacity: assessmentKeySaving ? 0.75 : 1 }}
                  >
                    {assessmentKeySaving ? 'Applying…' : 'Apply Assessment Credit (60 days)'}
                  </button>
                  {assessmentKeyMessage && <span style={{ fontSize: 12, fontWeight: 700, color: assessmentKeyMessage.type === 'success' ? '#216E4E' : '#C9372C' }}>{assessmentKeyMessage.text}</span>}
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '0 20px 20px' }}>
            <button disabled={assessmentKeySaving} onClick={() => { if (!assessmentKeySaving) setEditionInfoOpen(false); }} style={{ border: `1px solid ${C.shellBorder}`, background: C.white, color: C.title, borderRadius: 6, padding: '8px 12px', fontSize: 13, fontWeight: 600, cursor: assessmentKeySaving ? 'wait' : 'pointer', opacity: assessmentKeySaving ? 0.7 : 1 }}>Close</button>
            <button onClick={() => { window.location.href = 'mailto:info@metricus.com'; }} style={{ border: `1px solid ${C.shellBorder}`, background: C.white, color: C.title, borderRadius: 6, padding: '8px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Email Metricus</button>
            {isFreePlan && !isServiceNowRuntime && <button onClick={openPaidMarketplaceApp} style={{ border: `1px solid ${C.shellBorder}`, background: C.white, color: C.title, borderRadius: 6, padding: '8px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Paid Marketplace App</button>}
          </div>
          {assessmentKeySaving && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 3, background: 'rgba(255,255,255,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
              <style>{`@keyframes metricusAssessmentSpin { to { transform: rotate(360deg); } }`}</style>
              <div style={{ width: '100%', maxWidth: 360, border: '1px solid #CCE0FF', borderRadius: 12, background: '#F4F8FF', boxShadow: '0 8px 20px rgba(9,30,66,0.12)', padding: 20, textAlign: 'center' }}>
                <div style={{ width: 34, height: 34, margin: '0 auto 14px', border: '4px solid #DEEBFF', borderTopColor: C.primary, borderRadius: '50%', animation: 'metricusAssessmentSpin 0.8s linear infinite' }} />
                <div style={{ color: C.title, fontSize: 16, fontWeight: 800, marginBottom: 6 }}>Applying Assessment Credit</div>
                <div style={{ color: C.body, fontSize: 13, lineHeight: 1.4 }}>{assessmentApplyStep || 'Authorising the Assessment Credit…'}</div>
                <div style={{ color: C.muted, fontSize: 12, marginTop: 10 }}>This can take up to 10 seconds.</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPricingButton = () => {
    if (hasActiveSubscription || isSampleDataset || hasAssessmentCreditDatasetUnlock) return null;
    return (
      <button
        type="button"
        aria-label='Access and billing information'
        title='Access & Billing'
        onClick={(event) => { event.stopPropagation(); setEditionInfoOpen(true); }}
        style={{ height: 24, minWidth: 62, borderRadius: 999, border: `1px solid ${C.primaryBorder}`, background: C.white, color: C.primary, fontSize: 11, fontWeight: 700, lineHeight: 1, cursor: 'pointer', padding: '0 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, whiteSpace: 'nowrap' }}
      >
        Access & Billing
      </button>
    );
  };

  const renderBillingCreditsPill = () => null;

  const renderEditionStatus = () => {
    if (!effectiveEditionAccess || hasAssessmentCreditDatasetUnlock || isSampleDataset) return null;
    return (
      <div title={planLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button"
          onClick={(event) => { if (!hasActiveSubscription) { event.stopPropagation(); setEditionInfoOpen(true); } }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 9px', borderRadius: 999, border: `1px solid ${planTone.border}`, background: planTone.bg, color: planTone.text, fontSize: 11, fontWeight: 700, lineHeight: 1, letterSpacing: '0.02em', whiteSpace: 'nowrap', cursor: hasActiveSubscription ? 'default' : 'pointer' }}
          title={hasActiveSubscription ? planLabel : (isServiceNowRuntime ? 'View Assessment Credit options' : 'View Free Plan details')}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: planTone.dot, boxShadow: `0 0 0 2px ${planTone.halo}` }} />
          {planLabel}
        </button>
        {!hasActiveSubscription && renderPricingButton()}
      </div>
    );
  };

  return (
    <div style={{ width: '100%', flexShrink: 0 }}>
      {renderEditionInfoModal()}
      {accessSuccessMessage && (
        <div style={{ position: 'fixed', top: 18, right: 24, zIndex: 10050, background: '#E3FCEF', border: '1px solid #ABF5D1', color: '#216E4E', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 700, boxShadow: '0 8px 18px rgba(9,30,66,0.16)' }}>
          {accessSuccessMessage}
        </div>
      )}

      {/* ── Full header — hidden when collapsed ──────────────────── */}
      {!hidePrimaryHeader && !headerCollapsed && (
        <>
          <AppHeader
            iconSrc={resolvedIcon}
            title={appTitle}
            subtitle={modelTitle || undefined}
            actions={
              <>
                {renderEditionStatus()}
                <IconButton icon={ExitIcon} label="Models" onClick={onExit} />
                <IconButton icon={SupportIcon} label="Support" onClick={openSupportPortal} />
                <IconButton icon={HelpIcon} label="Help" onClick={onHelp} />
              </>
            }
            tabs={NAV}
            activeTab={activePrimary}
            onTabClick={(id) => handlePrimaryClick(NAV.find(n => n.id === id))}
          />
          {isTrialPlan && (
            <div style={{ padding: '0 20px 8px', background: C.white, color: C.muted, fontSize: 12, lineHeight: 1.4, textAlign: 'right' }}>
              Trial access is limited to the first 250 work items per model refresh.
            </div>
          )}
        </>
      )}

      {(showSubTabs || headerCollapsed || hidePrimaryHeader) && (
        <div style={{ padding: '8px 20px', background: C.panelBg, borderTop: '1px solid #F0F1F3' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>

            {/* ── Collapse/expand toggle — always in this spot ── */}
            <button
              onClick={toggleHeader}
              title={headerCollapsed ? 'Show header' : 'Hide header'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 6,
                border: `1px solid ${C.panelBorder}`,
                background: C.white,
                color: C.muted,
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0,
                marginRight: 4,
              }}
            >
              {headerCollapsed ? <ChevronDownIcon size={18} /> : <ChevronUpIcon size={18} />}
            </button>

            {/* ── Section label — visible only when header is collapsed ── */}
            {headerCollapsed && (() => {
              const Icon = activePrimaryMeta.icon;
              return (
                <>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '0 10px',
                    height: 28,
                    borderRadius: 6,
                    background: C.primaryTint,
                    border: `1px solid ${C.primaryBorder}`,
                    color: C.primary,
                    fontSize: 13,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>
                    {Icon && <Icon size={15} />}
                    {activePrimaryMeta.label}
                  </div>
                  <div style={{ width: 1, height: 18, background: C.panelBorder, flexShrink: 0 }} />
                </>
              );
            })()}

            {activePrimaryMeta.grouped
              ? /* ── Grouped dropdowns ── */
                (isFreeJiraProduct && activePrimary === 'consultant'
                  ? [
                      { label: 'Process Health Check', children: [{ id: 'quickHealthcheck', label: 'Process Health Check' }] },
                      { label: 'Automation & Optimization', children: [{ id: 'aiIntelligence', label: 'Automation & Optimization' }] },
                      { label: 'Process Assessment Report', children: [{ id: 'consultingReport', label: 'Process Assessment Report' }] },
                    ]
                  : activePrimaryMeta.groups || ADVISOR_GROUPS).map(group => {
                  // Filter children that require task attributes
                  const filtered = {
                    ...group,
                    children: group.children.filter(c => (!c.requiresAttrs || hasTaskAttributes) && (!c.capability || getCurrentCapabilities()[c.capability])),
                  };
                  if (filtered.children.length === 0) return null;
                  return (
                    <GroupDropdown
                      key={group.label}
                      group={filtered}
                      activeSub={activeSub}
                      onSelect={handleSubClick}
                    />
                  );
                })
              : /* ── Flat sub-tabs for other primaries ── */
                (isFreeJiraProduct && activePrimary === 'actions'
                  ? visibleSubTabs.filter(sub => sub.id === 'consultingReport')
                  : visibleSubTabs).map((sub) => {
                  const active = activeSub === sub.id;
                  return (
                    <button
                      key={sub.id}
                      onClick={() => handleSubClick(sub.id)}
                      style={{
                        minHeight: 34,
                        padding: '0 12px',
                        borderRadius: 8,
                        border: active ? `1px solid ${C.primaryBorder}` : '1px solid transparent',
                        background: active ? '#FFFFFF' : 'transparent',
                        color: active ? C.primary : C.body,
                        fontSize: 13,
                        fontWeight: active ? 700 : 600,
                        cursor: 'pointer',
                      }}
                    >
                      {sub.label}
                    </button>
                  );
                })
            }

            {/* ── Action buttons: same row, pushed right ── */}
            {showActionRow && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexShrink: 0 }}>
                {((activePrimary === 'consultant') || (activePrimary === 'actions' && activeSub === 'consultingReport')) && !isFreeJiraProduct && onSnapshotsClick && (
                  <SnapshotButton count={snapshotCount} onClick={onSnapshotsClick} />
                )}

                {!isFreeJiraProduct && (activePrimary === 'consultant' || activePrimary === 'evidence') && (
                  <CanvasActionButton
                    icon={SaveFindingIcon}
                    label="Save Finding"
                    onClick={handleSaveFinding}
                    tone="finding"
                    badgeCount={findingCount}
                  />
                )}

                {activePrimary === 'actions' && activeSub !== 'consultingReport' && (
                  <>
                    <CanvasActionButton
                      icon={SaveIcon}
                      label={workbenchSaveState === 'saving' ? 'Saving...' : 'Save Assessment'}
                      onClick={handleSaveWorkbench}
                      disabled={workbenchSaveState === 'saving'}
                      emphasis={workbenchSaveState !== 'saving'}
                    />
                  </>
                )}

                {!isFreeJiraProduct && activePrimary === 'value' && (
                  <>
                    <CanvasActionButton
                      icon={SaveFindingIcon}
                      label="Save Finding"
                      onClick={handleSaveValueFinding}
                      tone="finding"
                      badgeCount={findingCount}
                    />
                    <CanvasActionButton
                      icon={SaveIcon}
                      label="Save Value Scenario"
                      onClick={handleSaveValueScenario}
                      emphasis
                    />
                  </>
                )}

                {activePrimary === 'analysis' && (
                  <>
                    <CanvasActionButton
                      icon={ResetFiltersIcon}
                      label="Reset Filters"
                      onClick={handleResetFilters}
                      disabled={!(filterIconActive || discoveryIconActive)}
                      tone={(filterIconActive || discoveryIconActive) ? 'danger' : 'default'}
                    />
                    <CanvasActionButton
                      icon={SaveDiscoveryRulesIcon}
                      label="Save Discovery Rule"
                      onClick={handleSaveDiscoveryRules}
                      disabled={!(filterIconActive || discoveryIconActive)}
                    />
                    <CanvasActionButton
                      icon={ExportHtmlIcon}
                      label="Export"
                      onClick={handleExportHtml}
                    />
                    {!isFreeJiraProduct && <CanvasActionButton
                      icon={SaveFindingIcon}
                      label="Save Finding"
                      onClick={handleSaveFinding}
                      tone="finding"
                      badgeCount={findingCount}
                    />}
                    <CanvasActionButton
                      icon={SaveIcon}
                      label="Save Explorer"
                      onClick={handleSaveModel}
                      emphasis
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const SnapshotButton = ({ count = 0, onClick }) => (
  <button
    onClick={onClick}
    title="Advisor Snapshots"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 7,
      height: 34,
      padding: '0 12px',
      borderRadius: 8,
      border: `1px solid ${C.primaryBorder}`,
      background: '#FFFFFF',
      color: C.body,
      cursor: 'pointer',
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}
  >
    <SnapshotIcon size={16} />
    Advisor Snapshots
    {count > 0 && (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        padding: '0 6px',
        borderRadius: 999,
        background: C.primary,
        color: '#FFFFFF',
        fontSize: 11,
        fontWeight: 700,
      }}>
        {count}
      </span>
    )}
  </button>
);

const CanvasActionButton = ({ icon: Icon, label, onClick, disabled = false, emphasis = false, tone = 'default', badgeCount = 0 }) => (
  <button
    onClick={onClick}
    title={label}
    disabled={disabled}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 7,
      height: 32,
      padding: '0 10px',
      borderRadius: 8,
      border: emphasis
        ? `1px solid ${C.primary}`
        : tone === 'danger'
          ? `1px solid ${C.dangerBorder}`
          : tone === 'finding'
            ? `1px solid ${C.findingBorder}`
            : `1px solid ${C.panelBorder}`,
      background: emphasis
        ? C.primary
        : tone === 'danger' && !disabled
          ? C.dangerTint
          : tone === 'finding' && !disabled
            ? C.findingBg
            : '#FFFFFF',
      color: disabled
        ? C.muted
        : emphasis
          ? '#FFFFFF'
          : tone === 'danger'
            ? C.dangerText
            : tone === 'finding'
              ? C.findingText
              : C.body,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 12,
      fontWeight: 700,
      boxShadow: tone === 'finding' && !disabled ? '0 1px 2px rgba(9, 30, 66, 0.08)' : undefined,
      opacity: disabled ? 0.55 : 1,
      whiteSpace: 'nowrap',
    }}
  >
    <Icon size={16} />
    {label}
    {badgeCount > 0 && (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        padding: '0 6px',
        borderRadius: 999,
        background: tone === 'finding' ? C.primary : C.body,
        color: '#FFFFFF',
        fontSize: 11,
        fontWeight: 800,
        lineHeight: 1,
      }}>
        {badgeCount}
      </span>
    )}
  </button>
);

const IconButton = ({ icon: Icon, label, onClick }) => {
  const isBack = label === 'Models';
  return (
    <button
      onClick={onClick}
      title={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isBack ? 8 : 0,
        minWidth: isBack ? 'auto' : 36,
        width: isBack ? 'auto' : 36,
        height: 36,
        padding: isBack ? '0 12px' : 0,
        borderRadius: 10,
        border: `1px solid ${C.panelBorder}`,
        background: '#FFFFFF',
        color: isBack ? C.body : C.muted,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: isBack ? 600 : 500,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={18} />
      {isBack ? label : null}
    </button>
  );
};

export default AppNav;
